/**
 * SupabaseAuthContext - Provides auth state and token for API calls
 * Provides auth state with shape: userId, isSignedIn, isLoaded, getToken, user, signOut
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import { supabase } from '../../lib/supabase'
import { logger } from '../../helpers/logger.helper'
import { authLiveDebug } from '../../signIn/helpers/authLiveDebug.helper'
import type { Session, User } from '@supabase/supabase-js'
import { API_URL, API_ENDPOINTS } from '../../../constants/api'
import { useQueryClient } from '@tanstack/react-query'
import { Platform } from 'react-native'
import {
  mapSupabaseUser,
  type SupabaseAuthUser,
} from '../helpers/supabaseAuthUser.mapper'
import {
  extractSupabaseAuthProviders,
  getPrimarySupabaseAuthProvider,
} from '../helpers/extractSupabaseAuthProviders.helper'
import { getAuthUserClientObservabilityFields } from '../helpers/authObservability.helper'
import { reportAuthSessionToNewRelic } from '../helpers/reportAuthSessionToNewRelic.helper'
import { useNativeSdkInit } from './NativeSdkInitContext'
import { unregisterDeviceTokenOnSignOut } from '../../notifications/helpers/pushRegistration.helper'
import { useEntryIntentStore } from '../../onboarding/stores/entryIntentStore'

export type { SupabaseAuthUser }

interface SupabaseAuthContextValue {
  userId: string | undefined
  isSignedIn: boolean
  isAnonymous: boolean
  isLoaded: boolean
  getToken: (opts?: { skipCache?: boolean }) => Promise<string | null>
  refreshAccessToken: () => Promise<string | null>
  user: SupabaseAuthUser | null
  session: Session | null
  signOut: () => Promise<void>
}

const SupabaseAuthContext = createContext<SupabaseAuthContextValue | undefined>(undefined)

// Never leave the app gated on the black initial skeleton if the Supabase SDK's
// initial session read stalls (lock contention / stalled native storage). After
// this window we unblock the UI as signed-out.
const AUTH_INIT_TIMEOUT_MS = 5000

export function SupabaseAuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const { bridgeReady } = useNativeSdkInit()
  const [session, setSession] = useState<Session | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const sessionRef = useRef<Session | null>(null)
  const authIdentityRef = useRef<string>('signed-out')
  const initialSessionObservabilityReportedRef = useRef(false)
  sessionRef.current = session

  const emitAuthSessionObservability = useCallback((authEvent: string, s: Session | null) => {
    if (!s?.user) return
    const authProviders = extractSupabaseAuthProviders(s.user)
    const primaryAuthProvider = getPrimarySupabaseAuthProvider(s.user)
    const isAnonymous = s.user.is_anonymous === true
    const userFields = getAuthUserClientObservabilityFields(s.user)

    reportAuthSessionToNewRelic({
      authEvent,
      authProviders,
      primaryAuthProvider,
      isAnonymous,
      ...userFields,
    })
  }, [])

  const sessionUserObservability = useCallback((user: User | null | undefined) => {
    const fields = getAuthUserClientObservabilityFields(user)
    return { log: fields, debug: fields }
  }, [])

  const tryEmitInitialSessionObservability = useCallback(
    (s: Session | null) => {
      if (!s?.user || initialSessionObservabilityReportedRef.current) return
      initialSessionObservabilityReportedRef.current = true
      emitAuthSessionObservability('INITIAL_SESSION', s)
    },
    [emitAuthSessionObservability],
  )

  const buildAuthIdentity = useCallback((s: Session | null): string => {
    const nextUserId = s?.user?.id
    if (!nextUserId) return 'signed-out'
    return `${nextUserId}:${s.user.is_anonymous === true ? 'anon' : 'real'}`
  }, [])

  const resetAuthScopedCaches = useCallback(() => {
    queryClient.clear()
  }, [queryClient])

  useEffect(() => {
    if (Platform.OS === 'ios' && !bridgeReady) return

    let didInitialResolve = false
    const resolveLoaded = () => {
      didInitialResolve = true
      setIsLoaded(true)
    }

    // Initial session read. A rejected or hung getSession must never leave the app
    // gated on the black initial skeleton (AppContentWrapper waits on isLoaded), so
    // we both catch failures and arm the timeout fallback below.
    const initT0 = Date.now()
    supabase.auth
      .getSession()
      .then(({ data: { session: s }, error: e }) => {
        const obs = sessionUserObservability(s?.user ?? null)
        authLiveDebug('SupabaseAuth getSession done', {
          hasSession: !!s,
          isAnonymous: s?.user?.is_anonymous ?? null,
          error: e?.message ?? null,
          ms: Date.now() - initT0,
          ...obs.debug,
        })
        logger.info('[SupabaseAuth] getSession done', {
          hasSession: !!s,
          primaryAuthProvider: s?.user ? getPrimarySupabaseAuthProvider(s.user) : null,
          authProviders: s?.user ? extractSupabaseAuthProviders(s.user) : [],
          error: e?.message ?? null,
          ms: Date.now() - initT0,
          ...obs.log,
        })
        tryEmitInitialSessionObservability(s)
        setSession(s)
        sessionRef.current = s
        authIdentityRef.current = buildAuthIdentity(s)
        resolveLoaded()
      })
      .catch((err) => {
        authLiveDebug('SupabaseAuth getSession FAILED', {
          message: err instanceof Error ? err.message : 'Unknown error',
          ms: Date.now() - initT0,
        })
        logger.warn('[SupabaseAuth] getSession failed — proceeding as signed-out', {
          message: err instanceof Error ? err.message : 'Unknown error',
          ms: Date.now() - initT0,
        })
        resolveLoaded()
      })

    const initTimeout = setTimeout(() => {
      if (!didInitialResolve) {
        authLiveDebug('SupabaseAuth getSession TIMEOUT — proceeding as signed-out', {
          ms: Date.now() - initT0,
        })
        logger.warn('[SupabaseAuth] getSession timed out — proceeding as signed-out', {
          ms: Date.now() - initT0,
        })
        resolveLoaded()
      }
    }, AUTH_INIT_TIMEOUT_MS)

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      const obs = sessionUserObservability(s?.user ?? null)
      authLiveDebug('SupabaseAuth onAuthStateChange', {
        event,
        hasSession: !!s,
        isAnonymous: s?.user?.is_anonymous ?? null,
        msSinceInit: Date.now() - initT0,
        primaryAuthProvider: s?.user ? getPrimarySupabaseAuthProvider(s.user) : null,
        authProviders: s?.user ? extractSupabaseAuthProviders(s.user) : [],
        ...obs.debug,
      })
      const authObservabilityFields = {
        primaryAuthProvider: s?.user ? getPrimarySupabaseAuthProvider(s.user) : null,
        authProviders: s?.user ? extractSupabaseAuthProviders(s.user) : [],
        ...obs.log,
      }
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') {
        logger.warn('[SupabaseAuth] onAuthStateChange', {
          event,
          hasSession: !!s,
          msSinceInit: Date.now() - initT0,
          ...authObservabilityFields,
        })
      } else {
        logger.info('[SupabaseAuth] onAuthStateChange', {
          event,
          hasSession: !!s,
          msSinceInit: Date.now() - initT0,
          ...authObservabilityFields,
        })
      }
      if (event === 'SIGNED_IN' && s?.user) {
        emitAuthSessionObservability('SIGNED_IN', s)
      }
      if (event === 'INITIAL_SESSION') {
        tryEmitInitialSessionObservability(s)
      }
      setSession(s)
      sessionRef.current = s
      const nextIdentity = buildAuthIdentity(s)
      if (authIdentityRef.current !== nextIdentity) {
        authIdentityRef.current = nextIdentity
        resetAuthScopedCaches()
      }
      didInitialResolve = true
      setIsLoaded(true)
    })

    return () => {
      clearTimeout(initTimeout)
      subscription.unsubscribe()
    }
  }, [
    bridgeReady,
    buildAuthIdentity,
    resetAuthScopedCaches,
    emitAuthSessionObservability,
    tryEmitInitialSessionObservability,
    sessionUserObservability,
  ])

  const getToken = useCallback(async (opts?: { skipCache?: boolean }): Promise<string | null> => {
    if (opts?.skipCache) {
      authLiveDebug('SupabaseAuth getToken skipCache=true')
      const { data: { session: s } } = await supabase.auth.getSession()
      authLiveDebug('SupabaseAuth getToken skipCache result', { ok: !!s?.access_token })
      return s?.access_token ?? null
    }
    return sessionRef.current?.access_token ?? null
  }, [])

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const { data, error } = await supabase.auth.refreshSession()
    if (error) {
      logger.warn('Supabase refreshSession failed', {
        message: error.message,
      })
      return null
    }

    const nextSession = data.session
    if (nextSession) {
      setSession(nextSession)
      sessionRef.current = nextSession
    }

    return nextSession?.access_token ?? null
  }, [])

  const evictServerAuthCache = useCallback(async () => {
    const accessToken = sessionRef.current?.access_token
    if (!accessToken) {
      return
    }

    try {
      const response = await fetch(`${API_URL}${API_ENDPOINTS.auth.logout}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      if (!response.ok) {
        logger.warn('Server auth cache eviction failed', {
          status: response.status,
        })
      }
    } catch (error) {
      logger.warn('Server auth cache eviction request failed', {
        message: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }, [])

  const deleteAnonymousAccountBeforeSignOut = useCallback(async () => {
    const currentSession = sessionRef.current
    if (!currentSession?.user || currentSession.user.is_anonymous !== true) {
      return
    }
    const accessToken = currentSession.access_token
    if (!accessToken) {
      logger.warn('Anonymous sign-out deletion skipped: missing access token')
      return
    }
    try {
      const response = await fetch(`${API_URL}${API_ENDPOINTS.users.anonymousLogoutDelete}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      if (!response.ok) {
        logger.warn('Anonymous sign-out deletion endpoint failed', {
          status: response.status,
        })
      }
    } catch (error) {
      logger.warn('Anonymous sign-out deletion request failed', {
        message: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }, [])

  const clearPersistedWebSupabaseSession = useCallback(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return
    }
    try {
      const keysToRemove: string[] = []
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i)
        if (!key) continue
        if (key.startsWith('sb-') && key.includes('-auth-token')) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach((key) => window.localStorage.removeItem(key))
    } catch (error) {
      logger.warn('Failed to clear persisted Supabase web session keys', {
        message: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }, [])

  const signOut = useCallback(async () => {
    resetAuthScopedCaches()
    // Clear the persisted onboarding entry intent on logout (both the anonymous and
    // real-user paths below return early / independently). Without this a stale org /
    // squad-invite intent survives sign-out and re-drives onboarding routing for the
    // next session on this device. See entryIntentStore / entryIntentExpiry.helper.
    useEntryIntentStore.getState().clearIntent()
    const isAnonymousSession = sessionRef.current?.user?.is_anonymous === true

    if (isAnonymousSession) {
      await deleteAnonymousAccountBeforeSignOut()
      // Clear persisted session in the Supabase SDK's storage (AsyncStorage on
      // native, localStorage on web). 'local' scope avoids hitting the server —
      // the backend has already deleted the anonymous account.
      const { error: localSignOutError } = await supabase.auth.signOut({ scope: 'local' })
      if (localSignOutError) {
        logger.warn('Anonymous local sign-out returned error', {
          message: localSignOutError.message,
        })
      }
      clearPersistedWebSupabaseSession()
      setSession(null)
      sessionRef.current = null
      return
    }

    // Drop this device's push token while the access token is still valid, so a
    // signed-out user stops receiving pushes on a shared device. Best-effort.
    await unregisterDeviceTokenOnSignOut(sessionRef.current?.access_token)

    await evictServerAuthCache()

    const { error } = await supabase.auth.signOut({ scope: 'global' })
    if (error) {
      logger.warn('Global sign-out returned error — forcing local session clear', {
        message: error.message,
      })
      // Global sign-out can fail when the auth user has already been deleted
      // server-side (e.g. after account deletion). Fall back to wiping the local
      // session so the user is always logged out on this device.
      await supabase.auth.signOut({ scope: 'local' }).catch(() => {
        /* already cleared server-side; local wipe below handles state */
      })
    }
    // Always clear persisted tokens and in-memory session so the app immediately
    // reflects the signed-out state regardless of network/server errors above.
    clearPersistedWebSupabaseSession()
    setSession(null)
    sessionRef.current = null
  }, [clearPersistedWebSupabaseSession, deleteAnonymousAccountBeforeSignOut, evictServerAuthCache, resetAuthScopedCaches])

  const value: SupabaseAuthContextValue = {
    userId: session?.user?.id ?? undefined,
    isSignedIn: !!session?.user,
    isAnonymous: session?.user?.is_anonymous === true,
    isLoaded,
    getToken,
    refreshAccessToken,
    user: mapSupabaseUser(session?.user ?? null),
    session,
    signOut,
  }

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  )
}

export function useSupabaseAuth(): SupabaseAuthContextValue {
  const ctx = useContext(SupabaseAuthContext)
  if (ctx === undefined) {
    throw new Error('useSupabaseAuth must be used within SupabaseAuthProvider')
  }
  return ctx
}
